/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle, LinearProgress } from '@mui/material'
import { InvoicingProgress } from './use-invoicing-progress'

const styles = {
    body: css`
        min-width: 380px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-top: 8px;
    `,
    count: css`
        font-size: 20px;
        font-weight: 600;
    `,
    sub: css`
        font-size: 13px;
        color: rgba(0, 0, 0, 0.6);
        min-height: 18px;
    `,
}

type Props = {
    open: boolean
    progress: InvoicingProgress | null
}

export const InvoicingProgressDialog: React.FC<Props> = ({ open, progress }) => {
    const { t } = useTranslation()

    const total = progress?.total ?? 0
    const processed = progress?.processed ?? 0
    const percent = total > 0 ? Math.round((processed / total) * 100) : 0
    // Until the first event arrives (cache warm-up + initial queries), show an indeterminate bar.
    const indeterminate = !progress || total === 0

    return (
        <Dialog open={open} maxWidth="sm">
            <DialogTitle>{t('INVOICING_IN_PROGRESS', 'Creating invoices…')}</DialogTitle>
            <DialogContent>
                <div css={styles.body}>
                    <div css={styles.count}>
                        {total > 0
                            ? t('INVOICING_PROGRESS_COUNT', '{{processed}} of {{total}} companies', {
                                  processed,
                                  total,
                              })
                            : t('INVOICING_PREPARING', 'Preparing…')}
                    </div>
                    <LinearProgress
                        variant={indeterminate ? 'indeterminate' : 'determinate'}
                        value={percent}
                    />
                    <div css={styles.sub}>
                        {progress?.companyName
                            ? `${progress.companyName} — ${progress.outcome}`
                            : t('INVOICING_PLEASE_WAIT', 'Please keep this tab open until it finishes.')}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
